import { supportsMaxBridge, supportsPreparedShareMessage } from "./capabilities";
import { shareReferralInviteInMax } from "./max/referral-invite";
import { sharePreparedMessageInTelegram, useTelegramReferralInviteTracking } from "./telegram/referral-invite";

type TrackCustom = (event: string, params?: Record<string, unknown>) => void;

type ShareOutcome = "shared" | "cancelled" | "error" | "fallback";

type ShareResult = {
    outcome: ShareOutcome;
    errorCode?: string;
};

type GetTelegramMessageId = () => Promise<string | null>;

const TELEGRAM_TEXT = "Привет! Посмотри игру «Бизнесляндия». Они бесплатно раздают реальные подарки!";
const MAX_TEXT = `Привет! Игра в MAX дарит сертификаты Wildberries до 4000₽!

Всё бесплатно: участвуешь в розыгрышах ИЛИ собираешь 50 фрагментов и получаешь за них серт на 500₽.

Люди реально выигрывают каждый день!`;

const openTelegramShareUrlFallback = (refLink: string) => {
    const message = encodeURI(
        `${TELEGRAM_TEXT}\r\n${refLink}`,
    );

    const url = `https://t.me/share/url?url=${message}`;
    window.open(url);
};

export const useReferralInviteTracking = (trackCustom: TrackCustom) => {
    // Важно: не вызываем хуки условно. Платформенная реализация сама делает no-op, если не поддерживается.
    useTelegramReferralInviteTracking(trackCustom);
};

export const shareReferralInviteFallback = (refLink: string) => {
    if (supportsMaxBridge()) {
        // В MAX не используем t.me вообще. Пытаемся нативный shareContent с ссылкой.
        void shareReferralInviteInMax({ text: MAX_TEXT, link: refLink });
        return;
    }

    openTelegramShareUrlFallback(refLink);
};

export const shareReferralInvite = async (messageId: string, refLink: string): Promise<ShareResult> => {
    if (supportsMaxBridge()) {
        const result = await shareReferralInviteInMax({ text: MAX_TEXT, link: refLink });
        if (result.outcome === "unavailable") {
            // В MAX не делаем t.me fallback; максимально нейтральный вариант — просто ничего не шарить.
            // (Кнопка "Реферальная ссылка" уже позволяет пользователю скопировать ссылку вручную.)
            return { outcome: "fallback" };
        }
        return result.outcome === "error"
            ? { outcome: "error", errorCode: result.errorCode }
            : { outcome: result.outcome };
    }

    if (!supportsPreparedShareMessage()) {
        openTelegramShareUrlFallback(refLink);
        return { outcome: "fallback" };
    }

    try {
        await sharePreparedMessageInTelegram(messageId);
        return { outcome: "shared" };
    } catch {
        openTelegramShareUrlFallback(refLink);
        return { outcome: "fallback" };
    }
};

export const shareReferralInviteFlow = async (refLink: string, getTelegramMessageId: GetTelegramMessageId): Promise<ShareResult> => {
    if (supportsMaxBridge()) {
        const result = await shareReferralInviteInMax({ text: MAX_TEXT, link: refLink });
        if (result.outcome === "unavailable") {
            return { outcome: "fallback" };
        }

        return result.outcome === "error"
            ? { outcome: "error", errorCode: result.errorCode }
            : { outcome: result.outcome };
    }

    const messageId = await getTelegramMessageId();
    if (!messageId) {
        shareReferralInviteFallback(refLink);
        return { outcome: "fallback" };
    }

    return await shareReferralInvite(messageId, refLink);
};

